import React, { useState } from 'react';
import { User, Shield, Bookmark, Settings, LogOut, CheckCircle, Trash2, ExternalLink, Globe, Bell, Lock } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { AppUserSession, UserSavedCalculation, UserSettings, AppView } from '../../types';

interface UserProfileViewProps {
  onNavigateToView: (view: AppView) => void;
  onLogout: () => void;
}

export const UserProfileView: React.FC<UserProfileViewProps> = ({ onNavigateToView, onLogout }) => {
  const [session, setSession] = useState<AppUserSession>(authService.getSession());
  const [savedCalcs, setSavedCalcs] = useState<UserSavedCalculation[]>(authService.getSavedCalculations());
  const [settings, setSettings] = useState<UserSettings>(authService.getUserSettings());
  const [activeTab, setActiveTab] = useState<'calculations' | 'settings' | 'security'>('calculations');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const handleDeleteCalc = (id: string) => {
    authService.deleteSavedCalculation(id);
    setSavedCalcs(authService.getSavedCalculations());
  };

  const handleUpdateSettings = (newSettings: Partial<UserSettings>) => {
    const updated = authService.updateUserSettings(newSettings);
    setSettings({ ...updated });
    setSaveMessage('Paramètres mis à jour avec succès.');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-2xs flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-[#0E7044] text-white flex items-center justify-center font-black text-2xl shadow-md">
              {session.fullName.split(' ').map((n) => n[0]).join('').substring(0, 2)}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-black text-[#1A1A1A]">{session.fullName}</h1>
                <span className="px-3 py-1 bg-emerald-50 text-[#0E7044] text-xs font-bold rounded-full border border-emerald-200">
                  {session.role}
                </span>
              </div>
              <p className="text-sm text-[#4B5563] mt-1">{session.email}</p>
              <p className="text-xs text-[#9CA3AF] mt-0.5">Membre depuis {session.createdAt} • Dernière connexion: {session.lastLoginAt}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                authService.logout();
                onLogout();
              }}
              className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 font-bold text-sm rounded-xl transition-colors flex items-center gap-2 cursor-pointer border border-red-200"
            >
              <LogOut className="w-4 h-4" />
              <span>Se déconnecter</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-3 border-b border-[#E2E8F0] pb-2">
          <button
            onClick={() => setActiveTab('calculations')}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'calculations'
                ? 'bg-[#0E7044] text-white shadow-xs'
                : 'bg-white text-[#4B5563] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
            }`}
          >
            <Bookmark className="w-4 h-4" />
            <span>Calculs Sauvegardés ({savedCalcs.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'settings'
                ? 'bg-[#0E7044] text-white shadow-xs'
                : 'bg-white text-[#4B5563] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
            }`}
          >
            <Settings className="w-4 h-4" />
            <span>Préférences & Devise</span>
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'security'
                ? 'bg-[#0E7044] text-white shadow-xs'
                : 'bg-white text-[#4B5563] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span>Sécurité & 2FA</span>
          </button>
        </div>

        {saveMessage && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-[#0E7044] font-medium text-sm flex items-center gap-2">
            <CheckCircle className="w-5 h-5 shrink-0" />
            <span>{saveMessage}</span>
          </div>
        )}

        {/* Tab 1: Saved Calculations */}
        {activeTab === 'calculations' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#1A1A1A]">Vos Simulations & Calculs Enregistrés</h2>
              <span className="text-xs text-[#4B5563]">Synchronisés avec votre espace sécurisé YamooPay</span>
            </div>

            {savedCalcs.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#E2E8F0]">
                <Bookmark className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
                <h3 className="font-bold text-base text-[#1A1A1A]">Aucun calcul sauvegardé</h3>
                <p className="text-sm text-[#4B5563] mt-1">Utilisez les calculateurs de YamooPay et sauvegardez vos résultats pour les retrouver ici.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedCalcs.map((calc) => (
                  <div key={calc.id} className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-2xs hover:shadow-md transition-shadow flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <span className="px-2.5 py-1 bg-emerald-50 text-[#0E7044] font-bold text-xs rounded-lg uppercase">
                          {calc.calculatorType.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-[#9CA3AF]">{calc.createdAt}</span>
                      </div>
                      <h3 className="font-bold text-base text-[#1A1A1A] mb-2">{calc.title}</h3>
                      {calc.notes && <p className="text-xs text-[#4B5563] mb-4 italic">{calc.notes}</p>}

                      <div className="bg-[#F8FAFC] rounded-xl p-3 text-xs space-y-1 font-mono text-[#334155] border border-[#E2E8F0]">
                        <div>Entrées: {JSON.stringify(calc.inputs)}</div>
                        <div>Résultats: {JSON.stringify(calc.outputs)}</div>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
                      <button
                        onClick={() => {
                          if (calc.calculatorType === 'SALARY_TAX') onNavigateToView('salary-engine');
                          else if (calc.calculatorType === 'LOAN_MORTGAGE') onNavigateToView('loan-engine');
                          else if (calc.calculatorType === 'FX_TRANSFER') onNavigateToView('money-transfers');
                          else onNavigateToView('tools');
                        }}
                        className="text-xs font-bold text-[#0E7044] hover:underline flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>Ouvrir dans le calculateur</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCalc(calc.id)}
                        className="text-xs font-bold text-red-600 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Settings */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-2xs space-y-6 max-w-2xl">
            <h2 className="text-lg font-bold text-[#1A1A1A]">Préférences Personnalisées</h2>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-2">
                  Devise de Référence par Défaut
                </label>
                <div className="relative">
                  <Globe className="absolute left-3.5 top-3.5 w-4 h-4 text-[#9CA3AF]" />
                  <select
                    value={settings.defaultCurrency}
                    onChange={(e) => handleUpdateSettings({ defaultCurrency: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A] bg-white"
                  >
                    <option value="EUR">EUR (€) - Euro</option>
                    <option value="USD">USD ($) - US Dollar</option>
                    <option value="GBP">GBP (£) - British Pound</option>
                    <option value="XOF">XOF (CFA) - Franc CFA</option>
                    <option value="CAD">CAD ($) - Canadian Dollar</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-2">
                  Langue de l&apos;Interface
                </label>
                <select
                  value={settings.language}
                  onChange={(e) => handleUpdateSettings({ language: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A] bg-white"
                >
                  <option value="fr">Français (France / Afrique Francophone)</option>
                  <option value="en">English (Global)</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-[#0E7044]" />
                  <div>
                    <h4 className="font-bold text-sm text-[#1A1A1A]">Notifications par E-mail</h4>
                    <p className="text-xs text-[#4B5563]">Recevoir les alertes de taux FX et les rapports réglementaires</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleUpdateSettings({ emailNotifications: e.target.checked })}
                  className="w-5 h-5 text-[#0E7044] rounded border-gray-300 focus:ring-[#0E7044]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Security */}
        {activeTab === 'security' && (
          <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-2xs space-y-6 max-w-2xl">
            <h2 className="text-lg font-bold text-[#1A1A1A]">Sécurité du Compte & Authentification</h2>

            <div className="space-y-5">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-[#0E7044]" />
                  <div>
                    <h4 className="font-bold text-sm text-[#1A1A1A]">Authentification à Deux Facteurs (2FA)</h4>
                    <p className="text-xs text-[#4B5563]">Protection renforcée par code OTP / Application d&apos;authentification</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.twoFactorEnabled}
                  onChange={(e) => {
                    handleUpdateSettings({ twoFactorEnabled: e.target.checked });
                  }}
                  className="w-5 h-5 text-[#0E7044] rounded border-gray-300 focus:ring-[#0E7044]"
                />
              </div>

              <div className="p-5 border border-emerald-200 bg-emerald-50 rounded-xl space-y-3">
                <h4 className="font-bold text-sm text-[#0E7044] flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>Chiffrement & Conformité Bancaire</span>
                </h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Toutes vos données financières personnelles, calculs et préférences sont chiffrés de bout en bout (AES-256) et stockés conformément aux standards rigoureux RGPD & ISO 27001 de YamooPay.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
